import _ from 'lodash';
import {Fragment, useContext, useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import Select from 'react-select';
import {Job} from "../../types/Job";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {AuthUtility} from "../common/AuthUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {SelectOption} from "../common/CommonUIElementTypes";
import {DelayedSearchBar} from "../common/DelayedSearchBar";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {UrlUtility} from "../common/UrlUtility";
import {ListPaginationOptions} from '../common/ListPaginationOptions';
import {PillButton} from "../common/PillButton";
import {PhoneUtility} from "../common/PhoneUtility";
import {LocationUtility} from "../common/LocationUtility";
import {SelectUtility} from "../common/SelectUtility";

interface JobsListPageURLQueryParams {
    sortDirection?: string;
    page?: number;
    searchText?: string;
}

export const JobsListPage = function () {
    const context = useContext(GlobalContext);
    const urlBase = `/${Constants.routes.jobs.pathSegment}`;
    const dataStore = useRenderableDataStore({
        jobsList: [],
        loading: true,
        sortDirection: Constants.sortDirections.desc,
        searchText: '',
        pageCount: 0,
        selectedPage: 1,
        prevUrlQueryString: null,
    });
    const location = useLocation();

    // Update state if URL query string changes.
    if ((location && location.search) !== dataStore.data().prevUrlQueryString) {
        let selectedPage = 1;
        let sortDirection = Constants.sortDirections.desc;
        let searchText = '';

        let queryParams: JobsListPageURLQueryParams = UrlUtility.getQueryParamsFromSearchString(location.search);

        if (queryParams.sortDirection) {
            sortDirection = queryParams.sortDirection;
        }

        if (queryParams.page) {
            const parsedPage = parseInt('' + queryParams.page);
            selectedPage = Number.isInteger(parsedPage) && parsedPage >= 1 ? parsedPage : 1;
        }

        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }

        dataStore.updateAndRender({
            sortDirection: sortDirection,
            selectedPage: selectedPage,
            searchText: searchText,
            prevUrlQueryString: location.search,
        });
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadListDebounced();
        },
        [
            '' + dataStore.data().selectedPage,
            '' + dataStore.data().sortDirection,
            '' + dataStore.data().searchText
        ]
    );

    const syncDebounced = _.debounce(() => {
        dataStore.updateAndRender({loading: true})
        ApiUtility.syncJobs()
            .then(function (syncResponse: any) {
                if (syncResponse && syncResponse.error) {
                    dataStore.updateAndRender({loading: false});
                    MessageDisplayerUtility.error(syncResponse.error);
                    return;
                }
                const failures = (syncResponse && syncResponse.failures) || [];
                if (failures.length > 0) {
                    const failureLines = failures
                        .map((failure: { external_acumatica_id: string; display_name: string }) =>
                            `${failure.external_acumatica_id} -- ${failure.display_name}`)
                        .join('\n');
                    MessageDisplayerUtility.error(
                        `Job sync completed with ${failures.length} failure${failures.length === 1 ? '' : 's'}:\n${failureLines}`
                    );
                } else {
                    MessageDisplayerUtility.success('Job sync completed successfully.')
                }
                reloadListDebounced();
            })
            .catch(function (error: any) {
                dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while syncing jobs.`);
            });
    }, Constants.debounceTimeoutMs);

    const reloadListDebounced = _.debounce(() => {
        dataStore.updateAndRender({loading: true});
        ApiUtility.getJobsList(
            dataStore.data().selectedPage,
            dataStore.data().sortDirection,
            dataStore.data().searchText
        )
            .then(function (listData: any) {
                dataStore.updateAndRender({
                    jobsList: listData.list,
                    pageCount: listData.pageCount,
                    loading: false,
                });
            })
            .catch(function (error) {
                dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the jobs list.`);
            });
    }, Constants.debounceTimeoutMs);

    function handleSortChange(option: SelectOption) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {
            ...currentQueryParams,
            sortDirection: option.value,
            page: 1,
        };
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleSearchChange(updatedSearchText: string) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {
            ...currentQueryParams,
            page: 1,
        };
        updatedQueryParams.searchText = updatedSearchText || undefined;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function goToJobDetailPage(job: Job) {
        context.navigator.navigateTo(`${urlBase}/${job.id}`);
    }

    function renderJobsListDisplay(jobsList: Job[]) {
        let jobsListDisplay = [];

        for (let i = 0; i < jobsList.length; i++) {
            const job = jobsList[i];
            const deactivatedRowClass = !job.active ? ' deactivated' : '';
            const showSyncWarningIndicator = job.subsequent_auto_sync_failures >= 3
                && job.acumatica_id !== null && job.acumatica_id !== '';

            jobsListDisplay.push(
                <div
                    key={i + 1}
                    className={`common-list-row jobs-list-row${deactivatedRowClass}`}
                    onClick={() => goToJobDetailPage(job)}
                    data-job-id={job.id}
                >
                    <div className="common-list-row-content">
                        <div className="common-list-row-ticket-edge">
                            <div className="common-list-row-ticket-circle"/>
                        </div>
                        <div className="common-list-row-main">
                            <div className="jobs-list-row-job-number-cell">
                                <span className="jobs-list-row-job-number-text">{job.job_number}</span>
                            </div>
                            <span className="jobs-list-row-job-name">{job.display_name}</span>
                            {showSyncWarningIndicator && (
                                <img
                                    src="/static/images/warning-triangle-red.svg"
                                    className="common-list-row-sync-warning-indicator"
                                    title="Automatic sync is disabled for this job due to repeated errors. Successfully manually syncing this job will re-enable auto sync."
                                />
                            )}
                        </div>
                        <div className="common-list-row-details">
                            <div className="jobs-list-row-location-cell">
                                <img
                                    src="/static/images/location-pin-slate.svg"
                                    className="jobs-list-row-location-icon"
                                    aria-hidden="true"
                                />
                                <span className="jobs-list-row-location-text">
                                    {LocationUtility.locationLabel(job.location)}
                                </span>
                            </div>
                            <div className="jobs-list-row-foreman-cell">
                                {job.ordered_user_jobs && job.ordered_user_jobs.length > 0 ? (
                                    job.ordered_user_jobs.map((userJob) => (
                                        <div key={userJob.id} className="jobs-list-row-foreman-row">
                                            <span
                                                className="jobs-list-row-foreman-name"
                                            >{userJob.user.person.name}</span><span
                                            className="jobs-list-row-foreman-phone"
                                        >{PhoneUtility.dbPhoneToParenthesesDisplayPhone(userJob.user.person.phone)}</span>
                                        </div>
                                    ))
                                ) : (
                                    <span className="jobs-list-row-foreman-empty">No foremen assigned</span>
                                )}
                            </div>
                        </div>
                        <div className="common-list-row-action-indicators">
                            <div className="jobs-list-row-view-action-cell">
                                <span className="jobs-list-row-view-action-text">VIEW</span>
                            </div>
                            <div className="common-list-row-pointer-cell">
                                <img
                                    src="/static/images/arrow-right-tiny-green.svg"
                                    className="common-list-row-pointer-icon"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return jobsListDisplay;
    }

    return (
        <div className="common-list-page jobs-list-page">
            <div className="common-list-page-filters-header">
                <div className="common-list-page-filters-header-content">
                    <div className="common-list-page-filters-header-content-left">
                        <div className="common-list-sort-control-container">
                            <Select
                                className="common-list-sort-control"
                                classNamePrefix="common-list-sort-control"
                                options={Constants.jobsListSortOptions}
                                value={SelectUtility.selectOptionForId(
                                    dataStore.data().sortDirection,
                                    Constants.jobsListSortOptions
                                )}
                                onChange={(option) => handleSortChange(option as SelectOption)}
                                components={{
                                    Control: (controlProps: any) => (
                                        <div
                                            {...(controlProps.innerProps || {})}
                                            className="sort-filter-control"
                                        >
                                            <img
                                                className="common-list-page-filter-icon sort-filter-icon"
                                                src={
                                                    dataStore.data().sortDirection === Constants.sortDirections.asc
                                                        ? "/static/images/sort-ascending-textcolor.svg"
                                                        : "/static/images/sort-descending-textcolor.svg"
                                                }
                                            />
                                            <img
                                                className="common-list-page-filter-caret sort-caret"
                                                src="/static/images/caret-textcolor.svg"
                                            />
                                            <div className="sort-filter-control-hidden-children">
                                                {controlProps.children}
                                            </div>
                                        </div>
                                    )
                                }}
                            />
                        </div>
                    </div>
                    <div className="common-list-page-filters-header-content-right">
                        <DelayedSearchBar
                            value={dataStore.data().searchText}
                            performSearch={handleSearchChange}
                            placeholder="Search jobs"
                        />
                        {AuthUtility.userHasOneOfThesePermissions([Constants.permissionNames.jobUpdate]) && (
                            <PillButton
                                onClick={syncDebounced}
                                buttonText="SYNC"
                                buttonClasses="sync-button black-border-button"
                                containerClasses="sync-button-container"
                                rightIconSrc="/static/images/refresh-textcolor.svg"
                                disabled={dataStore.data().loading}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className="common-list-page-content">
                <ConditionalRenderer
                    if={dataStore.data().loading}
                    renderer={() => <OverlayLoadingIndicator/>}
                />
                {(() => {
                    if (dataStore.data().jobsList.length > 0) {
                        return (
                            <Fragment>
                                <div className="common-list-content jobs-list-content">
                                    {renderJobsListDisplay(dataStore.data().jobsList)}
                                </div>
                                <ListPaginationOptions
                                    pageCount={dataStore.data().pageCount}
                                    selectedPage={dataStore.data().selectedPage}
                                    urlBase={urlBase}
                                />
                            </Fragment>
                        );
                    } else if (!dataStore.data().loading) {
                        return (
                            <div className="common-empty-list-message-container">
                                <div className="common-empty-list-message">No jobs found.</div>
                            </div>
                        );
                    }
                })()}
            </div>
        </div>
    );
};
